<template>
  <div class="box">
    <div
      :class="{
        'container-bubble': true,
        'cursor-pointer': isReceivedAudio,
        'text-align-right': isRightToLeft,
      }"
      ref="contentContainer"
      @click="playAudio"
    >
      <SingleMessageChatText :is-source-playing :chat-contents :is-right-to-left :is-keep-all="isEveryLanguageSpaced" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import SingleMessageChatText from '@/components/domains/chat/chat-single/SingleMessageChatText.vue';
import { useMessagesSingle } from '@/composables/domains/chat/useMessagesSingle';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useAudioStore } from '@/stores/audio';
import { useMicrophoneStore } from '@/stores/microphone';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { ChatMessage, IAudioKey, MessageContent, MessageText } from '@/types/socket';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { ij_callLogId, ij_isLastIsOwn, ij_languageArea } from '@/utils/injectionKeys.js';

const { message } = defineProps<{
  message: ChatMessage;
}>();

const languageArea = inject(ij_languageArea) as ComputedRef<string>;

const { t } = useI18n();
const audioStore = useAudioStore();
const microphoneStore = useMicrophoneStore();
const userStore = useUserStore();
const toastStore = useToastStore();

const contentContainer = ref<HTMLElement>();
const isSourcePlaying = ref(false);
const isTargetPlaying = ref(false);
const chatContentBank = ref<Array<MessageContent>>([]); // 채팅 내용 저장소
const languageAreaBank = ref<Array<LanguageCode>>([]); // 지금까지 선택된 언어

const { updateContainerHeight, shrinkBubble } = useMessageTextCommon({
  contentContainer,
});
const { languageOptions } = storeToRefs(audioStore);

const isEveryLanguageSpaced = computed(() => {
  return languageAreaBank.value.every((code) => !isLanguageNoSpace(code));
});

// 오른쪽 정렬 언어인지 반환
const isRightToLeft = computed(() => {
  const listLangAlignRight: LanguageCode[] = ['ar']; // 아랍어,
  return listLangAlignRight.some((lang) => languageAreaBank.value.includes(lang));
});

// 지금까지 선택한 언어 중에 확정된게 있는지
const isComplete = computed(() => {
  const completedLang = languageAreaBank.value.find((labItem) => message[labItem].isComplete);
  return !!completedLang;
});

// 글자 크기 배율
const fontMultiplier = computed(() => userStore.userPreferences.textSizeMultiplier);

// 출력될 채팅 내용
const chatContents = computed(() => {
  const contents = languageAreaBank.value.flatMap((langCode) => message[langCode]?.contents ?? []) ?? [];

  return contents;
});

// 오디오 수신 여부
const isReceivedAudio = computed(() => {
  if (!isLastMessage.value) return true;
  const isAudioComplete = message.isAudioComplete === true;
  const isReset = message.reset === '1';
  return isAudioComplete || isReset;
});

// 나의 발화 중 이 메세지가 최신인지
const isLastMessage = computed(() => {
  const messageLastIsOwn = messageList.value.at(-1);
  if (!messageLastIsOwn) return false;
  return messageLastIsOwn.callLogId === message.callLogId;
});

// 수동재생
const playAudio = async () => {
  if (languageAreaBank.value.length === 1 && !languageOptions.value[languageAreaBank.value[0]].voiceOutputEnabled) {
    toastStore.error(t('not_support_audio_play'));
    return;
  }

  if (isSourcePlaying.value === true) {
    //ui 즉시 변경(중복 요청 방지)
    isSourcePlaying.value = false;
    audioStore.setPlayState(0, message.callLogId); //재생 중이면 정지
    return;
  }
  const audioKeys: IAudioKey[] = []; // 이 메세지에 포함된 모든 언어의 오디오 파일

  // 지금까지 선택된 언어들의 오디오파일 종합
  languageAreaBank.value.forEach((lab) => {
    const audioKeysExtra = message[`${lab}AudioKeys`];
    if (audioKeysExtra && audioKeysExtra.length > 0) {
      audioKeys.push(...audioKeysExtra);
    }
  });

  // 오디오 파일이 있는지 확인
  if (audioKeys && audioKeys.length > 0) {
    const { callLogId } = message;

    if (microphoneStore.isMicOn) {
      // 수동재생 중일 때 마이크 일시중지
      await microphoneStore.stopMic();
      const watcherAudioEnd = watch(
        () => audioStore.playState,
        async (newVal) => {
          if (newVal === 0 && !microphoneStore.isMicOn) {
            await microphoneStore.startMic(microphoneStore.selectedMicrophone.id);
            watcherAudioEnd();
          }
        }
      );
      audioStore.controllVolume();
      console.warn('playVolume', audioStore.playVolume);
    }

    audioStore.setAudioManulSrcs(false, callLogId, audioKeys);
  } else {
    console.warn('해당 언어 오디오 파일이 없습니다');
  }
};

// 아이콘 초기화
const resetIcons = () => {
  isSourcePlaying.value = false;
  isTargetPlaying.value = false;
};

updateContainerHeight(20 * fontMultiplier.value);

const { messageList } = useMessagesSingle();

//재생 ui표시
watch(
  () => [audioStore.playState, audioStore.playCallLogId],
  ([newVal1, newVal2]) => {
    if (newVal1 == 1) {
      if (newVal2 === message.callLogId) {
        isSourcePlaying.value = true;
        isTargetPlaying.value = false;
      } else {
        resetIcons();
      }
    } else if (newVal1 == 2) {
      if (newVal2 === message.callLogId) {
        isSourcePlaying.value = false;
        isTargetPlaying.value = true;
      } else {
        resetIcons();
      }
    } else {
      resetIcons();
    }
  }
);

// 언어가 바뀌어도 지금까지의 모든 메세지가 출력되게끔
watch(
  languageArea,
  (newVal: string) => {
    if (!languageAreaBank.value.includes(newVal) && isLastMessage.value && !isComplete.value)
      languageAreaBank.value.push(newVal);

    chatContentBank.value = languageAreaBank.value.flatMap((langCode) => {
      const contents = message[langCode]?.contents;
      return (
        contents?.map((content: MessageText) => ({
          ...content,
          highlight: false,
        })) ?? []
      );
    });
  },
  { immediate: true }
);

// 메세지 내용이 바뀔 때마다 높이 업데이트
watch(
  () => message,
  () => {
    if (isLastMessage.value) updateContainerHeight(20 * fontMultiplier.value);
  }
);

// 내가 더이상 마지막 상대 발화가 아니고 버블에 공백이 있을 때 버블 줄이기 발동
watch(
  isLastMessage,
  (newVal) => {
    if (!newVal) shrinkBubble(); // 버블 줄이기
  },
  { flush: 'post' }
);

// 글자 크기 변경 시 높이 조절
watch(fontMultiplier, () => {
  shrinkBubble(); // 버블 줄이기
});

provide(ij_callLogId, message.callLogId);
provide(ij_isLastIsOwn, isLastMessage);
</script>

<style scoped lang="scss">
.text-align-right {
  text-align: right;
}

.cursor-pointer {
  cursor: pointer;
}

.loading {
  width: fit-content;
  margin-bottom: 0.75rem;
  padding: 0.25rem 1.5rem 0.75rem 1rem;
  border-radius: 8px;
}

.box {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: fit-content;
  min-height: unset;
  padding: 0 1rem 1rem 1rem;
}

.container-bubble {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
}

::v-deep(.additional-info) {
  position: relative;
  flex-direction: row-reverse;
  height: 1.5rem;
  bottom: unset;
}

::v-deep(.top) {
  top: unset;
}
</style>
